/**
 * Geometry of forecast model grids, shared by the wind API (worker/) and the app. Pure, dependency-free:
 * the worker bundles it too.
 */

/** Area in degrees. `e` may exceed 180 (and `w` go below -180) around the antimeridian. */
export interface LngLatBounds {
  w: number;
  s: number;
  e: number;
  n: number;
}

/** Points (i, j) at lon0 + i·dLon, lat0 + j·dLat; row 0 is the southmost. */
export interface LatLonGrid {
  kind: 'latlon';
  nx: number;
  ny: number;
  lon0: number;
  lat0: number;
  dLon: number;
  dLat: number;
  /** Spans the whole circle of longitude, so column nx is column 0 again. */
  wraps: boolean;
}

/**
 * Lambert conformal conic on a sphere, tangent at `lat1` (HRRR). Point (i, j) is at x0 + i·dx, y0 + j·dy
 * metres. The API turns grid-relative wind to east/north, so clients only need this to place the points.
 */
export interface LccGrid {
  kind: 'lcc';
  nx: number;
  ny: number;
  lat1: number;
  lon0: number;
  radius: number;
  x0: number;
  y0: number;
  dx: number;
  dy: number;
}

export type ModelGrid = LatLonGrid | LccGrid;

const RAD = Math.PI / 180;

const param = (wkt: string, name: string) => {
  const m = new RegExp(`PARAMETER\\["${name}",(-?[\\d.]+)\\]`).exec(wkt);
  if (!m) throw new Error(`No ${name} in CRS`);
  return Number(m[1]);
};

/**
 * Grid geometry from a model's `crs_wkt` (as in Open-Meteo's latest.json) and the [ny, nx] dimensions of its
 * variables. The WKT's BBOX is [south, west, north, east] of the first and last grid points.
 */
export function gridFromMeta(wkt: string, ny: number, nx: number): ModelGrid {
  const bbox = /BBOX\[([^\]]+)\]/.exec(wkt)?.[1].split(',').map(Number);
  if (!bbox || bbox.length !== 4) throw new Error('No BBOX in CRS');
  const [s, w, n, e] = bbox;
  if (wkt.startsWith('GEOGCRS["WGS 84"')) {
    const dLon = (e - w) / (nx - 1);
    const dLat = (n - s) / (ny - 1);
    return { kind: 'latlon', nx, ny, lon0: w, lat0: s, dLon, dLat, wraps: Math.abs(nx * dLon - 360) < 1e-6 };
  }
  if (wkt.includes('Lambert Conic Conformal')) {
    const lat1 = param(wkt, 'Latitude of 1st standard parallel');
    if (lat1 !== param(wkt, 'Latitude of 2nd standard parallel')) throw new Error('Secant LCC not supported');
    if (param(wkt, 'Latitude of false origin') !== 0) throw new Error('LCC origin off the equator');
    const radius = Number(/ELLIPSOID\["Sphere",([\d.]+)/.exec(wkt)?.[1]);
    const unit = {
      kind: 'lcc' as const,
      nx,
      ny,
      lat1,
      lon0: param(wkt, 'Longitude of false origin'),
      radius,
    };
    const scale = { x0: 0, y0: 0, dx: 1, dy: 1 };
    const p0 = lccForward({ ...unit, ...scale }, w, s);
    const p1 = lccForward({ ...unit, ...scale }, e, n);
    return { ...unit, x0: p0[0], y0: p0[1], dx: (p1[0] - p0[0]) / (nx - 1), dy: (p1[1] - p0[1]) / (ny - 1) };
  }
  throw new Error(`Unsupported CRS: ${wkt.slice(0, 40)}`);
}

function lccConsts(g: Pick<LccGrid, 'lat1' | 'radius'>) {
  const n = Math.sin(g.lat1 * RAD);
  const f = (Math.cos(g.lat1 * RAD) * Math.tan(Math.PI / 4 + (g.lat1 * RAD) / 2) ** n) / n;
  // The origin lies on the equator: its distance from the cone's apex.
  return { n, rf: g.radius * f, rho0: g.radius * f };
}

/** Metres (x, y) of a point in an LCC grid's projection. */
export function lccForward(g: LccGrid, lng: number, lat: number): [number, number] {
  const { n, rf, rho0 } = lccConsts(g);
  const rho = rf / Math.tan(Math.PI / 4 + (lat * RAD) / 2) ** n;
  const theta = n * (lng - g.lon0) * RAD;
  return [rho * Math.sin(theta), rho0 - rho * Math.cos(theta)];
}

/** Longitude and latitude of a point (x, y metres) of an LCC grid's projection. */
export function lccInverse(g: LccGrid, x: number, y: number): [number, number] {
  const { n, rf, rho0 } = lccConsts(g);
  const rho = Math.sign(n) * Math.hypot(x, rho0 - y);
  const theta = Math.atan2(x, rho0 - y);
  const lat = 2 * Math.atan((rf / rho) ** (1 / n)) - Math.PI / 2;
  return [g.lon0 + theta / n / RAD, lat / RAD];
}

/** Angle (radians) from grid north to true north at a longitude: grid-relative wind is turned by it. */
export const lccRotation = (g: LccGrid, lng: number) => Math.sin(g.lat1 * RAD) * (lng - g.lon0) * RAD;

/** Longitude and latitude of grid point (i, j); indices may be fractional. */
export function gridLngLat(g: ModelGrid, i: number, j: number): [number, number] {
  if (g.kind === 'latlon') return [g.lon0 + i * g.dLon, g.lat0 + j * g.dLat];
  return lccInverse(g, g.x0 + i * g.dx, g.y0 + j * g.dy);
}

/**
 * Fractional grid indices (i, j) of a point. For grids around the world, i is taken in the turn of the
 * world closest to `lng` as given, so it may be negative or beyond nx.
 */
export function gridIndex(g: ModelGrid, lng: number, lat: number): [number, number] {
  if (g.kind === 'latlon') return [(lng - g.lon0) / g.dLon, (lat - g.lat0) / g.dLat];
  const [x, y] = lccForward(g, lng, lat);
  return [(x - g.x0) / g.dx, (y - g.y0) / g.dy];
}
